/**
 * GateCheckModal — the FULL KIT at takeoff, made interactive (2026-09-10, Matt).
 *
 * When the tower puts a plane in the air and its gate (docs/GATES.md) is not cleared, the rope used to
 * refuse with a plain message naming the open items. Matt: "make the list appear in the block dialogue
 * and interactive so the manager who is putting the plane in the air can go through the full kit items."
 * So this is that list, live: every gate item with In hand / Not required (reason) / Clock started /
 * Put it back — the same actions and the same server writes as the Gate node in the outline
 * (MilestoneGateSection.jsx) — and a "Continue" button that lights up the moment the gate clears, which
 * hands off to the crew picker.
 *
 * This REPLACED the old Full-Kit checklist category (Airtable "Pre-Work") as the launch check. Those
 * items are hidden (HIDDEN_CL_CATEGORIES) and no longer counted; the gate is the full kit, and the only
 * one. A milestone type with no gate template passes straight through.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { getMilestoneGate, setGateItemStatus, startGateWait, GATE_SOURCE_LABELS } from '../services/gates.js'
import { promptDialog } from './Dialog.jsx'
import { PALETTE } from './MiniChart.jsx'

const fmtDate = (s) => s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'

export default function GateCheckModal({ milestoneId, name, projectName, onClose, onCleared, zIndex = 800 }) {
  const [gate, setGate] = useState(undefined)   // undefined loading · null no gate · object
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')

  useEffect(() => {
    let alive = true
    setGate(undefined); setErr('')
    getMilestoneGate(milestoneId).then(r => { if (alive) setGate(r && r.gate ? r.gate : null) }).catch(e => { if (alive) { setErr(String(e && e.message || e)); setGate(null) } })
    return () => { alive = false }
  }, [milestoneId])

  // No gate on this type → nothing to check; hand straight through.
  useEffect(() => { if (gate === null && !err) onCleared() }, [gate, err, onCleared])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const who = () => ({ memberId: wf.currentMemberId || null, memberName: (ctx.currentMember && ctx.currentMember.fields && (ctx.currentMember.fields['Member Name'] || ctx.currentMember.fields['Name'])) || null })
  async function run(key, fn) {
    setBusy(key); setErr('')
    try { const r = await fn(); if (r && r.gate) setGate(r.gate) }
    catch (e) { setErr(String(e && e.message || e)) }
    finally { setBusy('') }
  }
  const inHand = (it) => run(it.id, () => setGateItemStatus(it.id, 'complete', who()))
  const putBack = (it) => run(it.id, () => setGateItemStatus(it.id, 'open', who()))
  const clock = (it) => run(it.id, () => startGateWait(it.id))
  async function notRequired(it) {
    const reason = await promptDialog({ title: 'Not required on this project', message: 'Why does this project not need it? One sentence. It stays on the record.', placeholder: 'e.g. No HOA on this parcel', multiline: true, required: true })
    if (reason === null) return
    run(it.id, () => setGateItemStatus(it.id, 'not_required', { ...who(), reason }))
  }

  const items = (gate && gate.items) || []
  const open = items.filter(i => i.status === 'open')
  const cleared = !!(gate && (gate.cleared_at || (items.length && open.length === 0)))

  return createPortal(
    <div style={{ ...S.backdrop, zIndex }} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()} role="dialog" aria-label="Full kit at takeoff">
        <div style={S.head}>
          <div>
            <div style={S.eyebrow}>Full kit at takeoff</div>
            <div style={S.title}>{name}{projectName ? <span style={S.proj}> · {projectName}</span> : null}</div>
          </div>
          <button style={S.x} onClick={onClose} title="Close">×</button>
        </div>
        {gate === undefined && <div style={S.note}>Reading the gate…</div>}
        {gate && (
          <>
            <div style={S.sub}>
              <b>{gate.label || 'Gate'}</b> · {items.length - open.length} of {items.length} in hand
              {cleared ? <span style={{ color: PALETTE.green }}> · cleared</span> : <span style={{ color: PALETTE.amber }}> · {open.length} still open</span>}
            </div>
            <div style={S.list}>
              {items.map(it => {
                const isDone = it.status === 'complete', isNR = it.status === 'not_required' || it.status === 'went_without'
                const late = it.is_wait && it.expected_by && !isDone && !isNR && new Date(it.expected_by + 'T00:00:00') < new Date()
                return (
                  <div key={it.id} style={{ ...S.item, opacity: busy === it.id ? 0.6 : 1 }}>
                    <span style={{ ...S.dot, background: isDone ? PALETTE.green : isNR ? 'var(--aj-border-mid)' : PALETTE.amber }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ ...S.itemName, textDecoration: isDone || isNR ? 'line-through' : 'none', color: isDone || isNR ? 'var(--aj-text-muted)' : 'var(--aj-text)' }}>{it.name}</div>
                      <div style={S.itemMeta}>
                        <span style={{ color: it.source_kind === 'direction' ? '#b7791f' : 'var(--aj-text-muted)' }}>{GATE_SOURCE_LABELS[it.source_kind] || it.source_kind}</span>
                        {it.summary && !isDone && !isNR ? <span> · {it.summary}</span> : null}
                        {isDone && it.completed_by_name ? <span> · in hand, {it.completed_by_name}</span> : null}
                        {isNR ? <span> · {it.status === 'went_without' ? 'went ahead without it' : 'not required'}{it.not_required_reason ? ': ' + it.not_required_reason : ''}{it.not_required_by_name ? ' · ' + it.not_required_by_name : ''}</span> : null}
                        {it.note ? <span style={{ fontStyle: 'italic' }}> · {it.note}</span> : null}
                        {it.is_wait && !isDone && !isNR ? (it.started_at
                          ? <span style={{ color: late ? PALETTE.red : undefined }}> · clock started {fmtDate(it.started_at)}, expected by {fmtDate(it.expected_by)}{late ? ', late' : ''}</span>
                          : <span> · a wait, usually {it.lead_time_days} days{it.lead_time_safe_days ? `, up to ${it.lead_time_safe_days}` : ''}</span>) : null}
                      </div>
                    </div>
                    <div style={S.actions}>
                      {!isDone && <button style={S.btn} disabled={!!busy} onClick={() => inHand(it)}>In hand</button>}
                      {!isDone && !isNR && <button style={S.ghost} disabled={!!busy} onClick={() => notRequired(it)}>Not required…</button>}
                      {it.is_wait && !isDone && !isNR && !it.started_at && <button style={S.ghost} disabled={!!busy} onClick={() => clock(it)}>Clock started</button>}
                      {(isDone || isNR) && <button style={S.ghost} disabled={!!busy} onClick={() => putBack(it)}>Put back</button>}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
        {err && <div style={S.err}>{err}</div>}
        <div style={S.foot}>
          <span style={S.footNote}>{cleared ? 'Gate cleared. Pick the crew next.' : 'Every item must be in hand or marked not required before this plane takes off.'}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.ghost} onClick={onClose}>Not now</button>
            <button style={{ ...S.primary, opacity: cleared ? 1 : 0.45 }} disabled={!cleared || !!busy} onClick={onCleared}>Continue to crew</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(7,39,45,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { width: '100%', maxWidth: 640, maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, boxShadow: '0 16px 48px rgba(0,0,0,0.35)', overflow: 'hidden' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '14px 16px 8px' },
  eyebrow: { fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' },
  title: { fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 15, color: 'var(--aj-text)', marginTop: 2 },
  proj: { fontWeight: 400, color: 'var(--aj-text-mid)' },
  x: { font: 'inherit', fontSize: 20, lineHeight: 1, background: 'transparent', border: 0, color: 'var(--aj-text-muted)', cursor: 'pointer', padding: '0 4px' },
  sub: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', padding: '0 16px 8px' },
  note: { fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 16px 16px' },
  list: { overflowY: 'auto', padding: '0 16px', flex: '1 1 auto', minHeight: 0 },
  item: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 0', borderTop: '0.5px solid var(--aj-border)' },
  dot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0, marginTop: 5 },
  itemName: { fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, lineHeight: 1.4 },
  itemMeta: { fontFamily: 'DM Sans, sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 2, lineHeight: 1.4 },
  actions: { display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 220 },
  btn: { font: 'inherit', fontFamily: 'DM Sans, sans-serif', fontSize: 12, padding: '5px 10px', borderRadius: 4, border: 'none', background: 'var(--aj-accent)', color: '#fff', cursor: 'pointer' },
  ghost: { font: 'inherit', fontFamily: 'DM Sans, sans-serif', fontSize: 12, padding: '5px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' },
  primary: { font: 'inherit', fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
  err: { fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: PALETTE.red, padding: '6px 16px' },
  foot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: '0.5px solid var(--aj-border)', flexWrap: 'wrap' },
  footNote: { fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', flex: '1 1 260px' },
}
